import { School } from '../types';

export const DEFAULT_SCHOOL_COLOUR = '#002366';

export interface SchoolColourPreset {
  name: string;
  hex: string;
  emoji: string;
}

export const SCHOOL_COLOUR_PRESETS: SchoolColourPreset[] = [
  { name: 'Navy Blue', hex: '#002366', emoji: '🔵' },
  { name: 'Royal Blue', hex: '#123D8D', emoji: '🔵' },
  { name: 'Sky Blue', hex: '#0284C7', emoji: '🔵' },
  { name: 'Emerald Green', hex: '#16A34A', emoji: '🟢' },
  { name: 'Forest Green', hex: '#15803D', emoji: '🟢' },
  { name: 'Crimson Red', hex: '#DC2626', emoji: '🔴' },
  { name: 'Maroon / Burgundy', hex: '#881337', emoji: '🔴' },
  { name: 'Purple / Violet', hex: '#7C3AED', emoji: '🟣' },
  { name: 'Amber / Orange', hex: '#EA580C', emoji: '🟠' },
  { name: 'Teal', hex: '#0D9488', emoji: '🟢' },
  { name: 'Slate Grey', hex: '#4B5563', emoji: '⚪' },
  { name: 'Charcoal Black', hex: '#1E293B', emoji: '⚫' }
];

/**
 * Returns the configured branding colour for a school, with backward-compatible defaults.
 */
export function getSchoolColour(
  school?: { colour?: string; color?: string; name?: string } | null,
  fallbackName?: string
): string {
  if (school?.colour && isValidHex(school.colour)) return school.colour;
  if (school?.color && isValidHex(school.color)) return school.color;

  const targetName = (school?.name || fallbackName || '').toLowerCase();
  if (targetName.includes('oasis')) return '#123D8D'; // Dark / Royal Navy
  if (targetName.includes('coomb')) return '#DC2626'; // Vibrant Red
  if (targetName.includes('eastfield')) return '#16A34A'; // Green
  if (targetName.includes('pilgrim')) return '#7C3AED'; // Purple
  if (targetName.includes('canon')) return '#0284C7'; // Sky / Blue

  return DEFAULT_SCHOOL_COLOUR;
}

/**
 * Validates a HEX color string.
 */
export function isValidHex(hex?: string): boolean {
  if (!hex) return false;
  return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hex.trim());
}

/**
 * Calculates whether text on top of this background color should be White or Dark for high-contrast legibility (WCAG).
 */
export function getContrastTextColor(hexColor?: string): '#FFFFFF' | '#0F172A' {
  if (!hexColor) return '#FFFFFF';
  let cleanHex = hexColor.trim().replace('#', '');
  if (cleanHex.length === 3) {
    cleanHex = cleanHex.split('').map((c) => c + c).join('');
  }
  if (cleanHex.length !== 6) return '#FFFFFF';

  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);

  // YIQ luminance formula:
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 145 ? '#0F172A' : '#FFFFFF';
}

/**
 * Maps a HEX color to an appropriate colored circle emoji for native select dropdown options.
 */
export function getSchoolColorEmoji(hexColor?: string): string {
  if (!hexColor) return '🔵';
  let cleanHex = hexColor.trim().replace('#', '');
  if (cleanHex.length === 3) {
    cleanHex = cleanHex.split('').map((c) => c + c).join('');
  }
  if (cleanHex.length !== 6) return '🔵';

  const r = parseInt(cleanHex.substring(0, 2), 16) / 255;
  const g = parseInt(cleanHex.substring(2, 4), 16) / 255;
  const b = parseInt(cleanHex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;

  if (max === min) {
    return l < 0.25 ? '⚫' : '⚪';
  }

  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

  let h = 0;
  if (max === r) {
    h = (g - b) / d + (g < b ? 6 : 0);
  } else if (max === g) {
    h = (b - r) / d + 2;
  } else {
    h = (r - g) / d + 4;
  }
  h /= 6;
  const deg = h * 360;

  if (s < 0.15) {
    return l < 0.3 ? '⚫' : '⚪';
  }

  if (deg >= 345 || deg < 15) return '🔴'; // Red
  if (deg >= 15 && deg < 45) return '🟠'; // Orange
  if (deg >= 45 && deg < 70) return '🟡'; // Yellow
  if (deg >= 70 && deg < 170) return '🟢'; // Green
  if (deg >= 170 && deg < 260) return '🔵'; // Blue / Cyan
  if (deg >= 260 && deg < 320) return '🟣'; // Purple / Violet
  return '🔴'; // Magenta / Maroon
}

/**
 * Returns a clean, shortened display name for table badges and tight spaces
 * e.g. "Oasis Academy Immingham" -> "Oasis Academy"
 * "Coomb Briggs Primary School" -> "Coomb Briggs"
 */
export function getSchoolShortName(fullName?: string): string {
  if (!fullName) return 'Unassigned';
  const trimmed = fullName.trim();

  // Known friendly abbreviations
  if (trimmed.toLowerCase().includes('oasis')) return 'Oasis Academy';
  if (trimmed.toLowerCase().includes('coomb')) return 'Coomb Briggs';
  if (trimmed.toLowerCase().includes('eastfield')) return 'Eastfield Primary';
  if (trimmed.toLowerCase().includes('canon peter hall')) return 'Canon Peter Hall';
  if (trimmed.toLowerCase().includes('pilgrim')) return 'Pilgrim Academy';

  // Generic cleaner
  const cleaned = trimmed
    .replace(/\b(Immingham)\b/gi, '')
    .replace(/\b(Primary School|Primary Academy|Secondary Academy|High School)\b/gi, '')
    .trim();

  return cleaned || trimmed;
}
